if (!customElements.get('expert-guides-journal')) {
  customElements.define('expert-guides-journal', class ExpertGuidesJournal extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.tabs = Array.from(this.querySelectorAll('[data-journal-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-journal-panel]'));
      this.mobile = window.matchMedia('(max-width: 1067px)');
      this.activePanel = this.panels.find(panel => !panel.hidden) || this.panels[0];
      const signal = this.controller.signal;
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.panels.forEach(panel => {
        const track = panel.querySelector('[data-journal-track]');
        track?.addEventListener('scroll', () => this.scheduleDots(panel), { signal, passive: true });
      });
      this.mobile.addEventListener('change', () => this.onResize(), { signal });
      document.addEventListener('shopify:block:select', event => this.onBlockSelect(event), { signal });
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this);
      this.onResize();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.controller = null;
    }

    onClick(event) {
      const tab = event.target.closest('[data-journal-tab]');
      if (tab && this.contains(tab)) {
        this.activate(tab.dataset.journalTab);
        return;
      }
      const dot = event.target.closest('[data-journal-dot]');
      if (dot && this.contains(dot)) {
        this.goTo(dot.closest('[data-journal-panel]'), Number(dot.dataset.journalDot), true);
      }
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-journal-tab]');
      if (tab && this.tabs.includes(tab)) {
        const index = this.tabs.indexOf(tab);
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this.activate(this.tabs[next].dataset.journalTab);
          this.tabs[next].focus({ preventScroll: true });
        }
        return;
      }
      if (!this.mobile.matches) return;
      const dot = event.target.closest('[data-journal-dot]');
      const track = event.target.closest('[data-journal-track]');
      if (!dot && event.target !== track) return;
      const panel = event.target.closest('[data-journal-panel]');
      if (!panel || panel !== this.activePanel) return;
      const cards = Array.from(panel.querySelectorAll('[data-journal-card]'));
      const index = dot ? Number(dot.dataset.journalDot) : this.nearestIndex(panel);
      let next;
      if (event.key === 'ArrowRight') next = Math.min(index + 1, cards.length - 1);
      if (event.key === 'ArrowLeft') next = Math.max(index - 1, 0);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = cards.length - 1;
      if (next !== undefined) {
        event.preventDefault();
        this.goTo(panel, next, true);
        if (dot) panel.querySelector(`[data-journal-dot="${next}"]`)?.focus({ preventScroll: true });
      }
    }

    activate(key) {
      const panel = this.panels.find(item => item.dataset.journalPanel === key);
      if (!panel) return;
      this.tabs.forEach(tab => {
        const selected = tab.dataset.journalTab === key;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(item => { item.hidden = item !== panel; });
      this.activePanel = panel;
      this.goTo(panel, 0, false);
      this.updateDots(panel, 0);
    }

    goTo(panel, index, animate) {
      const track = panel?.querySelector('[data-journal-track]');
      const card = panel?.querySelectorAll('[data-journal-card]')[index];
      if (!track || !card) return;
      const left = track.scrollLeft + card.getBoundingClientRect().left - track.getBoundingClientRect().left;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: this.mobile.matches ? left : 0, behavior: animate && !reducedMotion ? 'smooth' : 'instant' });
      this.updateDots(panel, index);
    }

    nearestIndex(panel) {
      const track = panel.querySelector('[data-journal-track]');
      if (!track || !this.mobile.matches) return 0;
      const edge = track.getBoundingClientRect().left;
      let nearest = 0;
      let distance = Infinity;
      panel.querySelectorAll('[data-journal-card]').forEach((card, index) => {
        const current = Math.abs(card.getBoundingClientRect().left - edge);
        if (current < distance) {
          distance = current;
          nearest = index;
        }
      });
      return nearest;
    }

    scheduleDots(panel) {
      if (panel !== this.activePanel) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateDots(panel, this.nearestIndex(panel)));
    }

    updateDots(panel, index) {
      panel.querySelectorAll('[data-journal-dot]').forEach(dot => {
        if (Number(dot.dataset.journalDot) === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    onResize() {
      this.panels.forEach(panel => {
        const track = panel.querySelector('[data-journal-track]');
        if (track) track.tabIndex = this.mobile.matches && panel.querySelectorAll('[data-journal-card]').length > 1 ? 0 : -1;
      });
      if (this.activePanel) this.scheduleDots(this.activePanel);
    }

    onBlockSelect(event) {
      const id = event.detail?.blockId;
      const block = Array.from(this.querySelectorAll('[data-journal-block]')).find(item => item.dataset.journalBlock === id);
      if (!block) return;
      const key = block.dataset.journalTab || block.closest('[data-journal-panel]')?.dataset.journalPanel;
      if (key) this.activate(key);
      if (block.matches('[data-journal-card]')) {
        const panel = block.closest('[data-journal-panel]');
        const index = Array.from(panel.querySelectorAll('[data-journal-card]')).indexOf(block);
        this.goTo(panel, index, false);
      }
    }
  });
}
